/**
 * ==========================================================================
 *  THE AUTHORITATIVE RUN
 * ==========================================================================
 *  Everything that decides how an attempt turns out, and nothing else. No
 *  three.js, no DOM, no wall clock, no Math.random(): give this class the same
 *  sequence of `StepInput`s twice and you get the same run twice, down to the
 *  millisecond.
 *
 *  That property is the whole anti-cheat design. The browser does not get to
 *  say how fast it went - it says which buttons were held and where the mouse
 *  was pointing, and the *server* runs this exact class over those inputs and
 *  computes the time itself. A forged POST with `timeMs: 1` does not survive
 *  contact with a re-simulation.
 *
 *  Which is also why `Game` must not have a second, subtly different copy of
 *  the step order. Game builds a StepInput and calls step(); so does the
 *  verifier. One code path, one answer.
 * ==========================================================================
 */

import { FIXED_TIMESTEP } from '../config/gameplay';
import { PinChallenge } from './PinChallenge';
import { pinFromButtons } from './StepInput';
import { GLASS_PAIN, HANNU } from '../config/gameplay';
import type { IntoxicationLevel } from '../config/intoxication';
import { BEER } from '../config/beer';
import { takeSip, type SipRoll } from '../player/Beer';
import { IntoxicationSystem } from '../player/IntoxicationSystem';
import { PlayerController } from '../player/PlayerController';
import { applyKnockback } from '../player/PlayerMovement';
import { RunState } from '../game/GameState';
import type { FinishedRun, RunManager } from '../game/RunManager';
import { Aamupala, type AamupalaEvents } from '../world/Aamupala';
import { CollisionWorld } from '../world/CollisionWorld';
import { Door, type DoorEvent } from '../world/Door';
import { FinishSeat } from '../world/FinishSeat';
import { Hannu } from '../world/Hannu';
import { Ikkuna } from '../world/Ikkuna';
import { Kamiina, type KlapiEvent } from '../world/Kamiina';
import { Sauna } from '../world/Sauna';
import { Sima, type TuoppiEvent } from '../world/Sima';
import { InteractionSystem, type InteractContext, type Interactable } from '../world/Interactable';
import type { LevelDefinition } from '../world/Level';
import { populateCollisionWorld } from '../world/buildCollision';
import type { Split } from '../world/Checkpoint';
import {
  BUTTON,
  angleFromUnits,
  clampPitchUnits,
  movementFromButtons,
  pickFromButtons,
  wrapYawUnits,
  type StepInput,
} from './StepInput';

/**
 * What happened during one step. Almost all of it is observational - the caller
 * turns these into sounds and HUD flashes, and ignoring them changes nothing.
 *
 * `glassBroke` is the exception, and it is flagged where it is declared: the
 * step that reports it has also already slowed the player down.
 */
export interface StepEvents {
  jumped: boolean;
  /**
   * True on the step the second press landed inside its window. `jumped` is
   * *not* also set - the two are different sounds and different prices, and
   * the caller should be able to tell them apart. See `DOUBLE_JUMP`.
   */
  doubleJumped: boolean;
  landed: boolean;
  impactSpeed: number;
  hannuEmerged: boolean;
  hannuShouted: boolean;
  hannuHit: boolean;
  /** Id of the interactable that was triggered this step, if any. */
  interacted: string | null;
  /**
   * Which way a leaf was sent on the step F landed on a door, or null.
   *
   * A door answers F for the whole run now, so `interacted` naming one is no
   * longer the same thing as a door opening - it swings the other way every
   * other press. This is what says which, and it is the only reason the
   * caller can tell an opening from a closing without asking the door. See
   * `Door`.
   */
  door: DoorEvent | null;
  /** Non-null on the step a klapi left the kasa, or went into the pesä. */
  klapi: KlapiEvent | null;
  /**
   * Non-null on the three steps the drink has: the one the empty tuoppi came
   * off the stack on (`'taken'`), the one a hana's handle went down on
   * (`'pouring'`), and the one two seconds later that the glass came up full
   * on (`'poured'`).
   *
   * Observational, with the same caveat `aamupala.done` carries: the step that
   * reports `'poured'` has also already taken the porukka's box out of the
   * corridor. The caller may ignore the field; the way to the chair is open
   * either way.
   */
  tuoppi: TuoppiEvent | null;
  /** Non-null on the step E went into the bottle. */
  sipped: SipRoll | null;
  /**
   * What the muurikka did this step, or null - which is almost every step.
   *
   * Observational like the rest of this struct, with one exception worth the
   * same warning `glassBroke` carries: the step that reports `done` has also
   * already taken Mäysä's collider out of the porstua. The caller may ignore
   * the field; the doorway is open either way.
   */
  aamupala: AamupalaEvents | null;
  /**
   * True on the single step the bemari's charging cable came out of its
   * socket. Latched afterwards - see `powerIsOut`.
   */
  powerCut: boolean;
  /**
   * True on the single step Kimmo put his fist through the pirtti's window. A
   * one-way door like `powerCut` - but unlike everything else on this struct
   * it is *not* purely observational: the same step opens the hole and sets
   * `hurtTimer`, so the punch is worth `GLASS_PAIN.duration` seconds at
   * `slowFactor`. The caller may ignore this field; the run has already been
   * changed either way.
   */
  glassBroke: boolean;
  splits: Split[];
  /** Non-null on the single step the player sat down. */
  finished: FinishedRun | null;
}

function noEvents(): StepEvents {
  return {
    jumped: false,
    doubleJumped: false,
    landed: false,
    impactSpeed: 0,
    hannuEmerged: false,
    hannuShouted: false,
    hannuHit: false,
    interacted: null,
    door: null,
    klapi: null,
    tuoppi: null,
    sipped: null,
    aamupala: null,
    powerCut: false,
    glassBroke: false,
    splits: [],
    finished: null,
  };
}

export class RunSimulation {
  readonly collision = new CollisionWorld();
  readonly aamupala: Aamupala | null;
  readonly doors: Door[] = [];
  readonly finishSeat: FinishSeat;
  readonly hannu: Hannu;
  readonly ikkuna: Ikkuna;
  readonly kamiina: Kamiina;
  readonly sauna: Sauna | null;
  readonly sima: Sima | null;
  readonly player: PlayerController;
  readonly interaction = new InteractionSystem();

  pin: PinChallenge;
  private powerCut = false;

  constructor(
    readonly level: LevelDefinition,
    private readonly runManager: RunManager,
    pinSeed = 0,
  ) {
    this.pin = new PinChallenge(pinSeed);
    const { doors, solids, ikkuna } = populateCollisionWorld(level, this.collision);

    for (const spec of level.doors) {
      const leaf = doors.get(spec.id);
      if (!leaf) continue;
      const door = new Door(spec, leaf);
      this.doors.push(door);
      this.interaction.register(door);
    }

    this.finishSeat = new FinishSeat(level.finish, () => this.onSit());
    this.interaction.register(this.finishSeat);

    const gate = solids.get(level.kamiina.gate);
    if (!gate) throw new Error(`level has no veräjä called ${level.kamiina.gate}`);
    this.kamiina = new Kamiina(level.kamiina, gate);
    this.interaction.register(this.kamiina.pile);
    this.interaction.register(this.kamiina.hatch);

    // Breakfast, and the man in the porstua who is the reason it is not
    // optional. Optional in the *level*, though: a level without a muurikka is
    // a level nobody has to eat in, and there is no half-state where the man
    // is there and the pan is not.
    if (level.aamupala) {
      const maysa = solids.get(level.aamupala.maysa.gate);
      if (!maysa) throw new Error(`level has no Mäysä called ${level.aamupala.maysa.gate}`);
      this.aamupala = new Aamupala(level.aamupala, maysa);
    } else {
      this.aamupala = null;
    }

    // And the drink, and the crowd in the last corridor who are the reason it
    // is not optional either. Optional in the *level* for the same reason
    // breakfast is: a level with nobody in the corridor is a level nobody has
    // to buy a round in, and there is no half-state where the porukka is there
    // and the tuoppi is not.
    if (level.sima) {
      const porukka = solids.get(level.sima.gate);
      if (!porukka) throw new Error(`level has no porukka called ${level.sima.gate}`);
      const additionalGates = (level.sima.additionalGates ?? []).map((name) => {
        const collider = solids.get(name);
        if (!collider) throw new Error(`level has no porukka called ${name}`);
        return collider;
      });
      this.sima = new Sima(level.sima, porukka, additionalGates);
      this.interaction.register(this.sima.glass);
      for (const tap of this.sima.taps) this.interaction.register(tap);
    } else {
      this.sima = null;
    }

    // And the sauna's door, which is a wall with a prompt on it and stays one:
    // the only thing F reaches for in this level that never gives way. Optional
    // in the level like the two above - a hall with no sauna behind it is a
    // hall with nothing to try - and registered the same way, so the HUD finds
    // it by the same route it finds a door. See `Sauna`.
    this.sauna = level.sauna ? new Sauna(level.sauna) : null;
    if (this.sauna) this.interaction.register(this.sauna);

    // The pane, which is a wall with a prompt on it until somebody punches it
    // out. Registered like everything else F answers to, so the HUD finds it
    // by the same route it finds a door.
    this.ikkuna = new Ikkuna(level.ikkuna, ikkuna);
    this.interaction.register(this.ikkuna);

    this.hannu = new Hannu({
      exitAngle: level.hotTub.hannuExitAngle,
      x: level.hotTub.center[0],
      z: level.hotTub.center[1],
      radius: level.hotTub.radius,
      rimRadius: level.hotTub.rimRadius,
      rimHeight: level.hotTub.rimHeight,
      gateX: level.kamiina.gatePost[0],
      gateZ: level.kamiina.gatePost[1],
      activation: level.hotTub.activation,
    });

    this.player = new PlayerController(
      this.collision,
      level.spawn,
      new IntoxicationSystem(undefined, runManager.intoxicationLevel),
    );
  }

  /** Full reset. The caller is responsible for resetting the RunManager too. */
  reset(pinSeed = this.pin.seed): void {
    for (const door of this.doors) door.reset();
    this.finishSeat.reset();
    this.kamiina.reset();
    this.aamupala?.reset();
    this.sima?.reset();
    this.sauna?.reset();
    this.hannu.reset();
    this.ikkuna.reset();
    this.player.intoxication.setLevel(this.runManager.intoxicationLevel);
    this.player.reset(this.level.spawn);
    this.pin = new PinChallenge(pinSeed);
    this.powerCut = false;
  }

  /**
   * Whether the yard's charging cable has been kicked out of its socket.
   *
   * Latched for the rest of the run: a cable dragged off a tolppa at 30 km/h
   * does not go back in, and a blackout that flickered back on would read as a
   * bug rather than as a consequence.
   *
   * Nothing downstream of this touches movement. It changes what the renderer
   * lights and what the mixer plays, which is why it can exist at all without
   * retiring a single leaderboard time.
   */
  get powerIsOut(): boolean {
    return this.powerCut;
  }

  /** The dose is part of the run's identity, so this always implies a restart. */
  setIntoxication(level: IntoxicationLevel): void {
    this.player.intoxication.setLevel(level);
  }

  /** What pressing F right now would trigger. Drives the HUD prompt. */
  focusedInteractable(): Interactable | null {
    if (this.finishSeat.isSeated) return null;
    return this.interaction.findTarget(this.interactContext());
  }

  interactContext(): InteractContext {
    return {
      eye: this.player.eyePosition,
      feet: this.player.state.position,
      look: this.player.lookDirection,
      simulationTime: this.runManager.simulationTime,
    };
  }

  /**
   * Exactly one fixed simulation step.
   *
   * The order matters and is part of the format contract - changing it changes
   * every stored replay, which is why `simulationFingerprint()` exists.
   */
  step(input: StepInput): StepEvents {
    const events = noEvents();
    if (this.runManager.state !== RunState.Running) return events;

    if (this.finishSeat.isSeated) {
      this.runManager.advanceTimer();
      this.pin.press(pinFromButtons(input.buttons));
      if (this.pin.complete) events.finished = this.runManager.finish();
      return events;
    }

    const state = this.player.state;

    // 1. Look. The *quantised* angle is what steers the player, in the live
    //    game and in the verifier alike - otherwise a replay would only ever
    //    be approximately right, and approximately right is not verifiable.
    state.yaw = angleFromUnits(wrapYawUnits(input.yawQ));
    state.pitch = angleFromUnits(clampPitchUnits(input.pitchQ));

    const simulationTime = this.runManager.simulationTime;

    // 2. Doors are simulation state (their colliders come and go), so they
    //    step before anything can collide with them. The veräjä at the palju
    //    is one more of exactly those, so it goes here too.
    for (const door of this.doors) door.simulate(simulationTime);
    this.kamiina.simulate(simulationTime);
    //    And the porukka in the juhlasali's last corridor, who are the fourth:
    //    a box that is solid until the tuoppi is full. Unlike the other three
    //    this one has a pour running through it, so it is handed the eye and
    //    the fixed step: the glass fills while Kimmo stands at the nozzle he
    //    opened and stops filling the moment he does not. See `Sima`.
    events.tuoppi = this.sima?.simulate(this.interactContext(), FIXED_TIMESTEP) ?? null;

    //    And Mäysä in the porstua, who is the third of exactly those: a box
    //    that is solid until breakfast says otherwise. He steps before
    //    anything can collide with him for the same reason a door does, and
    //    the three keys that feed him are read here rather than down in the
    //    interaction block because they are his and nothing else's - `1` in
    //    the pirtti is a no-op, not a sip.
    events.aamupala =
      this.aamupala?.simulate(
        state.position,
        this.runManager.stepCount,
        pickFromButtons(input.buttons),
      ) ?? null;

    // 3. Interaction. Edge-triggered: one bit, one step, one press.
    //
    //    F is the whole of the hand. A door, the chair, a klapi off the kasa,
    //    the same klapi through the hatch, the glass in the pirtti's window -
    //    whatever is nearest and reachable, and nothing at all when there is
    //    nothing there. Pressing it in an empty room is a no-op.
    if ((input.buttons & BUTTON.action) !== 0) {
      const context = this.interactContext();
      const target = this.interaction.findTarget(context);
      if (target) {
        // Read before the press, because the press is what changes it: with
        // wood in hand the hatch burns it, with empty hands the kasa fills
        // them, and both are the same interactable calling the same method.
        const held = this.kamiina.holdingKlapi;
        target.interact(context);
        events.interacted = target.id;
        if (target === this.kamiina.pile || target === this.kamiina.hatch) {
          events.klapi = held ? 'burned' : 'taken';
        }
        //  And a door, which needs no such reading either: it reverses on
        //  every press and answers F for the whole run, so which of the two
        //  things just happened is a question about where the leaf is headed
        //  *now* - and it is headed there already, one line above.
        if (target instanceof Door) events.door = target.isOpen ? 'opened' : 'closed';
        //  The tuoppi, which needs no such reading: the glass and the hanat are
        //  separate interactables, so which of the two things just happened is
        //  a question about *which one was pressed* rather than about what was
        //  in Kimmo's hand beforehand.
        //
        //  Note that neither press is `poured`. The hana only puts the handle
        //  down; the glass fills two seconds later, up in the block above, and
        //  that is the step the corridor opens on. The two can never land
        //  together - a hana that is running cannot be pressed and a full
        //  glass cannot be filled - so this may overwrite what `simulate`
        //  returned without ever losing an event.
        if (this.sima) {
          if (target === this.sima.glass) events.tuoppi = 'taken';
          else if (this.sima.taps.includes(target)) events.tuoppi = 'pouring';
        }
        //  The pane. Unlike every other event on `StepEvents` this one has
        //  already changed the run by the time it is reported: the collider is
        //  gone and Kimmo is bleeding. See `glassBroke`.
        if (target === this.ikkuna) {
          events.glassBroke = true;
          state.hurtTimer = GLASS_PAIN.duration;
        }
      }
    }

    //    E is the bottle and only the bottle. It reaches for nothing and it
    //    can never be shadowed by a door, which is the entire reason it is not
    //    on F with the rest: a swig is something Kimmo does with what is
    //    already in his hand, and it has to work identically in a doorway and
    //    in the middle of the yard.
    //
    //    Successful sips alternate sides; timing varies the remaining roll.
    //    Both are reproduced from replay inputs. See src/config/beer.ts.
    if ((input.buttons & BUTTON.sip) !== 0) {
      events.sipped = takeSip(this.player.state.beer, this.runManager.stepCount);
      if (events.sipped) {
        // Derive from the starting dose and count to avoid accumulated rounding
        // error. The run's category and replay header retain the starting dose.
        const initial = this.runManager.intoxicationLevel;
        this.player.intoxication.setLevel({
          ...initial,
          alcoholPromille: initial.alcoholPromille + state.beer.sips * BEER.promillePerSip,
        });
      }
    }

    // Sitting freezes the world, but the PIN challenge remains on the clock.
    if (this.finishSeat.isSeated) {
      this.runManager.advanceTimer();
      events.splits = this.runManager.completeCheckpoint('FINISH');
      return events;
    }

    // 4. Hannu moves first, so the player is shoved by where he actually is.
    const hannuEvent = this.hannu.simulate(
      simulationTime,
      FIXED_TIMESTEP,
      state.position,
      this.kamiina.isFed,
      // The klapi press happened a few lines up, in this same step, which is
      // what lets him answer the first log on the frame it lands rather than
      // on the one after it - the same promise the fed check has always made.
      events.klapi === 'burned',
    );
    events.hannuEmerged = hannuEvent.emerged;
    events.hannuShouted = hannuEvent.shouted;
    if (hannuEvent.hit) {
      applyKnockback(
        state,
        hannuEvent.hitDirectionX,
        hannuEvent.hitDirectionZ,
        HANNU.knockbackSpeed,
        HANNU.knockbackLift,
        HANNU.staggerDuration,
      );
      this.runManager.recordHannuHit();
      events.hannuHit = true;
    }

    // 5. The player.
    const result = this.player.step(
      movementFromButtons(input.buttons),
      simulationTime,
      FIXED_TIMESTEP,
      true,
    );

    // 6. The clock, then the checkpoints - in that order, so a checkpoint
    //    entered on this step is stamped with the time including this step.
    this.runManager.advanceTimer();
    if (events.aamupala?.done) {
      events.splits.push(...this.runManager.completeCheckpoint('AAMUPALA'));
    }
    if (events.tuoppi === 'poured') {
      events.splits.push(...this.runManager.completeCheckpoint('SIMA'));
    }
    events.splits.push(...this.runManager.updateCheckpoints(state.position));

    // Anything the player was pushed out of sideways this step. The only one
    // worth naming is the cable: it is the venue's power, so running through it
    // is how the juhlanavetta ends up dark.
    if (!this.powerCut && result.contacts.includes(this.level.powerCable)) {
      this.powerCut = true;
      events.powerCut = true;
    }

    events.jumped = state.jumpedThisStep;
    events.doubleJumped = state.doubleJumpedThisStep;
    events.landed = state.landedThisStep;
    events.impactSpeed = result.impactSpeed;
    return events;
  }

  private onSit(): void {
    this.player.state.velocity.x = 0;
    this.player.state.velocity.y = 0;
    this.player.state.velocity.z = 0;
  }
}
